<script lang="ts">
  import { onMount, tick } from "svelte";
  import EngagementInvitationDrawer from "./engagementInvitationDrawer.svelte";

  export let title = "Engagement Invitations";
  export let rejectionReasons: string[] = [
    "Customer problem unclear",
    "Next steps unclear",
    "Unable to support",
    "Duplicate of partner referral",
    "Other",
  ];
  export let onAccept: (row: { id?: string }) => Promise<any> = async () => ({
    success: false,
    message: "Accept action is not configured.",
  });
  export let onImportAcceptedOpportunity: (awsOpportunityId: string) => Promise<any> = async () => ({
    success: false,
    message: "Import action is not configured.",
  });
  export let onRefreshOpportunities: () => void = () => {};
  export let onSyncAcceptedOpportunityToHubspot: (localOpportunityId: number) => Promise<any> =
    async () => ({
      success: false,
      message: "HubSpot sync action is not configured.",
    });
  export let onReject: (row: {
    id?: string;
    rejectionReason?: string;
  }) => Promise<any> = async () => ({
    success: false,
    message: "Reject action is not configured.",
  });

  const apiBaseUrl = `${process.env.SVELTE_APP_REMOTE_URL}/api`;
  const aceBaseUrl = `${apiBaseUrl}/alace`;

  let invitations: any[] = [];
  let loading = false;
  let error = "";
  let search = "";

  let drawerOpen = false;
  let detailLoading = false;
  let detailError = "";
  let selectedSummary: any = null;
  let selectedInvitation: any = null;

  let actingId = "";
  let actingAction: "accept" | "reject" | null = null;
  let banner: { tone: "success" | "warning" | "error"; message: string } | null = null;
  let bannerRef: HTMLDivElement | null = null;

  let rejectDialogTarget: any = null;
  let rejectionReason = rejectionReasons[0] || "Other";

  let syncDialogTarget: any = null;
  let syncDialogResult: any = null;
  let syncingAcceptedOpportunity = false;

  const portal = (node: HTMLElement) => {
    document.body.appendChild(node);

    return {
      destroy() {
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      },
    };
  };

  $: if (!rejectionReasons.includes(rejectionReason)) {
    rejectionReason = rejectionReasons[0] || "Other";
  }

  const statusBadgeClass = (status?: string): string => {
    if (status === "ACCEPTED") return "bg-green-500/20 text-white border-green-400/50 shadow-[0_0_0_1px_rgba(74,222,128,0.1)]";
    if (status === "REJECTED") return "bg-red-500/20 text-white border-red-400/50 shadow-[0_0_0_1px_rgba(248,113,113,0.1)]";
    if (status === "EXPIRED") return "bg-slate-400/20 text-white border-slate-300/40 shadow-[0_0_0_1px_rgba(203,213,225,0.08)]";
    return "bg-amber-500/25 text-white border-amber-300/50 shadow-[0_0_0_1px_rgba(251,191,36,0.12)]";
  };

  const formatDate = (value?: string): string => {
    if (!value) return "—";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? value
      : parsed.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  const matchesSearch = (invitation: any): boolean => {
    if (!search.trim()) return true;

    const query = search.trim().toLowerCase();
    const haystack = [
      invitation.engagementTitle,
      invitation.senderCompanyName,
      invitation.senderAwsAccountId,
      invitation.receiverAlias,
      invitation.receiverAwsAccountId,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  };

  const extractErrorMessage = (payload: any, fallback: string) =>
    payload?.message || payload?.error || fallback;

  const normalizeImportSuccessMessage = (message?: string) => {
    const raw = String(message || "").trim();
    if (!raw) {
      return "The opportunity was imported successfully.";
    }

    return raw
      .replace(/\s+as local opportunity\s+\d+\.?/i, " successfully.")
      .replace(/\s+to local opportunity\s+\d+\.?/i, " successfully.")
      .replace(/\s{2,}/g, " ")
      .trim();
  };

  const fetchInvitations = async () => {
    loading = true;
    error = "";

    try {
      const filter = encodeURIComponent(
        JSON.stringify({
          limit: 100,
          order: ["invitationDate DESC"],
          where: { status: "PENDING" },
        })
      );
      const res = await fetch(`${aceBaseUrl}/engagement-invitations?filter=${filter}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(extractErrorMessage(data, `HTTP ${res.status}`));
      }

      invitations = Array.isArray(data) ? data : [];
    } catch (err: any) {
      invitations = [];
      error = err?.message || "Failed to load engagement invitations.";
    } finally {
      loading = false;
    }
  };

  const loadInvitationDetails = async (summary: any) => {
    if (!summary?.id) return;

    selectedSummary = summary;
    selectedInvitation = null;
    detailError = "";
    detailLoading = true;
    drawerOpen = true;

    try {
      const res = await fetch(`${aceBaseUrl}/aws-partner-central/get-engagement-invitation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Identifier: summary.id }),
      });
      const data = await res.json();

      if (!res.ok || !data?.success) {
        throw new Error(extractErrorMessage(data, `HTTP ${res.status}`));
      }

      selectedInvitation = data.invitation ?? null;
    } catch (err: any) {
      detailError = err?.message || "Failed to load invitation details.";
    } finally {
      detailLoading = false;
    }
  };

  const closeDrawer = () => {
    drawerOpen = false;
    selectedSummary = null;
    selectedInvitation = null;
    detailError = "";
  };

  const showBanner = (tone: "success" | "warning" | "error", message: string) => {
    banner = { tone, message };
    void tick().then(() => {
      bannerRef?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const getBannerTitle = (tone: "success" | "warning" | "error"): string => {
    if (tone === "success") return "Completed";
    if (tone === "warning") return "Action needed";
    return "Something went wrong";
  };

  const getBannerIcon = (tone: "success" | "warning" | "error"): string => {
    if (tone === "success") return "✓";
    if (tone === "warning") return "!";
    return "×";
  };

  const refreshInvitationState = async (invitationId?: string) => {
    await fetchInvitations();

    if (drawerOpen && invitationId && selectedSummary?.id === invitationId) {
      const updatedSummary =
        invitations.find((item) => item.id === invitationId) || selectedSummary;
      await loadInvitationDetails(updatedSummary);
    }
  };

  const normalizeActionResult = (actionLabel: string, result: any) => {
    if (result?.accepted && result?.imported === false) {
      return {
        tone: "warning" as const,
        message: result?.message || `${actionLabel} completed, but the import did not finish.`,
      };
    }

    if (result?.accepted) {
      return {
        tone: "success" as const,
        message: result?.message || `${actionLabel} completed successfully.`,
      };
    }

    if (result?.inProgress) {
      return {
        tone: "warning" as const,
        message: result?.message || `${actionLabel} is in progress.`,
      };
    }

    if (result?.success === false) {
      return {
        tone: "error" as const,
        message: result?.message || `${actionLabel} failed.`,
      };
    }

    return {
      tone: "success" as const,
      message: result?.message || `${actionLabel} completed successfully.`,
    };
  };

  const runAction = async (
    actionLabel: string,
    row: any,
    actionType: "accept" | "reject",
    fn: (row: any) => Promise<any>
  ) => {
    const rowId = row?.id || selectedSummary?.id || "";
    actingId = rowId;
    actingAction = actionType;
    banner = null;

    try {
      const result = await fn(row);
      const normalized = normalizeActionResult(actionLabel, result);
      showBanner(normalized.tone, normalized.message);
      await refreshInvitationState(rowId);
    } catch (err: any) {
      showBanner("error", err?.message || `Failed to run ${actionLabel}.`);
    } finally {
      actingId = "";
      actingAction = null;
    }
  };

  const openRejectDialog = (summary: any) => {
    rejectDialogTarget = summary;
    rejectionReason = rejectionReasons[0] || "Other";
  };

  const closeRejectDialog = () => {
    rejectDialogTarget = null;
  };

  const openSyncDialog = (summary: any, result: any) => {
    syncDialogTarget = summary;
    syncDialogResult = result;
  };

  const closeSyncDialog = () => {
    if (syncingAcceptedOpportunity) return;
    syncDialogTarget = null;
    syncDialogResult = null;
  };

  const handleAccept = (summary: any) => {
    void runAccept(summary);
  };

  const handleReject = (summary: any) => {
    openRejectDialog(summary);
  };

  const runAccept = async (summary: any) => {
    const rowId = summary?.id || selectedSummary?.id || "";
    actingId = rowId;
    actingAction = "accept";
    banner = null;

    try {
      const result = await onAccept(summary);

      if (result?.accepted && result?.task?.opportunityId) {
        openSyncDialog(summary, result);
        return;
      }

      const normalized = normalizeActionResult("Accept", result);
      showBanner(normalized.tone, normalized.message);
      await refreshInvitationState(rowId);
    } catch (err: any) {
      showBanner("error", err?.message || "Failed to run Accept.");
    } finally {
      actingId = "";
      actingAction = null;
    }
  };

  const deferAcceptedSync = async () => {
    if (!syncDialogTarget || !syncDialogResult) return;

    const rowId = syncDialogTarget.id;
    const message =
      syncDialogResult?.message ||
      "Invitation accepted. You can import it later with Sync from AWS and then sync it to HubSpot from the Opportunities table.";

    closeSyncDialog();
    showBanner("success", message);
    await refreshInvitationState(rowId);
  };

  const confirmAcceptedSync = async () => {
    if (!syncDialogTarget || !syncDialogResult?.task?.opportunityId) return;

    const rowId = syncDialogTarget.id;
    const acceptedMessage =
      syncDialogResult?.message ||
      "Invitation accepted. You can import it later with Sync from AWS and then sync it to HubSpot from the Opportunities table.";

    syncingAcceptedOpportunity = true;
    banner = null;
    let shouldRefreshOpportunities = false;

    try {
      const importResult = await onImportAcceptedOpportunity(syncDialogResult.task.opportunityId);

      if (importResult?.success && importResult?.opportunityId) {
        shouldRefreshOpportunities = true;
        const importMessage = normalizeImportSuccessMessage(importResult?.message);
        const hubspotResult = await onSyncAcceptedOpportunityToHubspot(importResult.opportunityId);

        if (hubspotResult?.hubspotId || hubspotResult?.status) {
          const hubspotMessage = "HubSpot sync completed successfully.";

          showBanner("success", `${acceptedMessage} ${importMessage} ${hubspotMessage}`);
        } else {
          showBanner(
            "warning",
            `${acceptedMessage} ${importMessage} ${extractErrorMessage(
              hubspotResult,
              "HubSpot sync failed. You can retry it later from the Opportunities table."
            )}`
          );
        }
      } else {
        showBanner(
          "warning",
          `${acceptedMessage} ${extractErrorMessage(
            importResult,
            "The opportunity could not be imported automatically."
          )}`
        );
      }
    } catch (err: any) {
      showBanner(
        "warning",
        `${acceptedMessage} ${err?.message || "The opportunity could not be synced automatically."}`
      );
    } finally {
      syncingAcceptedOpportunity = false;
      closeSyncDialog();
      if (shouldRefreshOpportunities) {
        onRefreshOpportunities();
      }
      await refreshInvitationState(rowId);
    }
  };

  const confirmReject = async () => {
    if (!rejectDialogTarget) return;

    const payload = {
      ...rejectDialogTarget,
      rejectionReason,
    };

    closeRejectDialog();
    await runAction("Reject", payload, "reject", onReject);
  };

  $: filteredInvitations = invitations.filter(matchesSearch);
  $: pendingCount = invitations.filter((item) => item.status === "PENDING").length;

  onMount(() => {
    void fetchInvitations();
  });
</script>

<div class="bg-[#24283a] border border-white/10 rounded-xl p-6 mb-4">
  <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
    <div>
      <h3 class="text-[1.125rem] font-bold text-white m-0 uppercase tracking-[0.5px]">
        {title}
      </h3>
      <div class="text-[0.813rem] text-white/60 mt-2 leading-6">
        Review AWS ACE opportunity invitations before importing them through the sync flow.
      </div>
    </div>

    <div class="flex flex-col sm:flex-row gap-3">
      <input
        bind:value={search}
        class="px-4 py-2 bg-[#1b1e2e] border border-white/20 text-white placeholder:text-white/50 rounded-md text-sm focus:outline-none focus:border-white focus:shadow-[0_0_0_3px_rgba(255,255,255,0.1)]"
        placeholder="Search title, sender, account..."
      />
      <button
        class="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-colors disabled:opacity-60"
        on:click={fetchInvitations}
        disabled={loading}
      >
        {loading ? "Refreshing..." : "Refresh"}
      </button>
    </div>
  </div>

  <div class="flex flex-wrap gap-3 mb-5">
    <div class="px-4 py-3 rounded-xl bg-black/20 border border-white/10 min-w-[140px]">
      <div class="text-xs uppercase tracking-[0.15em] text-white/60 mb-1">Pending</div>
      <div class="text-2xl font-bold text-white">{pendingCount}</div>
    </div>
    <div class="px-4 py-3 rounded-xl bg-black/20 border border-white/10 min-w-[140px]">
      <div class="text-xs uppercase tracking-[0.15em] text-white/60 mb-1">Visible</div>
      <div class="text-2xl font-bold text-white">{filteredInvitations.length}</div>
    </div>
  </div>

  {#if banner}
    <div
      bind:this={bannerRef}
      class="engagement-banner mb-5"
      class:engagement-banner--success={banner.tone === "success"}
      class:engagement-banner--warning={banner.tone === "warning"}
      class:engagement-banner--error={banner.tone === "error"}
    >
      <div class="engagement-banner__inner">
        <div
          class="engagement-banner__icon"
        >
          {getBannerIcon(banner.tone)}
        </div>

        <div class="engagement-banner__content">
          <p class="engagement-banner__eyebrow">
            {getBannerTitle(banner.tone)}
          </p>
          <p class="engagement-banner__message">
            {banner.message}
          </p>
        </div>

        <button
          class="engagement-banner__close"
          on:click={() => (banner = null)}
          aria-label="Dismiss message"
        >
          ✕
        </button>
      </div>
    </div>
  {/if}

  {#if error}
    <div class="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-red-100">
      {error}
    </div>
  {:else if loading && invitations.length === 0}
    <div class="rounded-xl border border-white/10 bg-black/20 px-4 py-12 text-center text-white/50">
      Loading engagement invitations...
    </div>
  {:else if filteredInvitations.length === 0}
    <div class="rounded-xl border border-white/10 bg-black/20 px-4 py-12 text-center text-white/50">
      No engagement invitations found.
    </div>
  {:else}
    <div class="overflow-x-auto">
      <table class="w-full border-collapse min-w-[880px]">
        <thead>
          <tr class="bg-black/20">
            <th class="px-4 py-3 text-left text-[0.813rem] font-bold text-white/90 uppercase tracking-[0.5px] border-b-2 border-white/10">
              Opportunity
            </th>
            <th class="px-4 py-3 text-left text-[0.813rem] font-bold text-white/90 uppercase tracking-[0.5px] border-b-2 border-white/10">
              Sender
            </th>
            <th class="px-4 py-3 text-left text-[0.813rem] font-bold text-white/90 uppercase tracking-[0.5px] border-b-2 border-white/10">
              Received
            </th>
            <th class="px-4 py-3 text-left text-[0.813rem] font-bold text-white/90 uppercase tracking-[0.5px] border-b-2 border-white/10">
              Expires
            </th>
            <th class="px-4 py-3 text-left text-[0.813rem] font-bold text-white/90 uppercase tracking-[0.5px] border-b-2 border-white/10">
              Status
            </th>
            <th class="px-4 py-3 text-right text-[0.813rem] font-bold text-white/90 uppercase tracking-[0.5px] border-b-2 border-white/10">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {#each filteredInvitations as invitation (invitation.id)}
            <tr
              class="border-b border-white/5 cursor-pointer transition-all hover:bg-[#24283a]/5"
              on:click={() => loadInvitationDetails(invitation)}
            >
              <td class="px-4 py-4 align-top">
                <div class="text-sm font-semibold text-white leading-6">
                  {invitation.engagementTitle || "Untitled Invitation"}
                </div>
                <div class="text-xs text-white/60 mt-2 break-all">{invitation.id || "—"}</div>
              </td>
              <td class="px-4 py-4 align-top">
                <div class="text-sm text-white">{invitation.senderCompanyName || "—"}</div>
                <div class="text-xs text-white/60 mt-2 break-all">
                  {invitation.senderAwsAccountId || "—"}
                </div>
              </td>
              <td class="px-4 py-4 align-top text-sm text-white/80">
                {formatDate(invitation.invitationDate)}
              </td>
              <td class="px-4 py-4 align-top text-sm text-white/80">
                {formatDate(invitation.expirationDate)}
              </td>
              <td class="px-4 py-4 align-top">
                <span
                  class={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${statusBadgeClass(
                    invitation.status
                  )}`}
                >
                  {invitation.status || "UNKNOWN"}
                </span>
              </td>
              <td class="px-4 py-4 align-top">
                <div class="flex justify-end gap-2">
                  {#if invitation.status === "PENDING"}
                    <button
                      class="px-3 py-2 rounded-md text-sm transition-colors disabled:opacity-60 bg-blue-600 text-white font-semibold hover:bg-blue-500"
                      on:click|stopPropagation={() => handleAccept(invitation)}
                      disabled={actingId === invitation.id || syncingAcceptedOpportunity}
                    >
                      {actingId === invitation.id && actingAction === "accept"
                        ? "Working..."
                        : "Accept"}
                    </button>
                    <button
                      class="px-3 py-2 rounded-md text-sm transition-colors disabled:opacity-60 border border-red-400/40 text-red-200 bg-red-500/10 hover:bg-red-500/15"
                      on:click|stopPropagation={() => handleReject(invitation)}
                      disabled={actingId === invitation.id || syncingAcceptedOpportunity}
                    >
                      {actingId === invitation.id && actingAction === "reject"
                        ? "Working..."
                        : "Reject"}
                    </button>
                  {:else}
                    <span class="text-xs text-white/50 uppercase tracking-[0.15em] self-center">
                      No action
                    </span>
                  {/if}
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

{#if drawerOpen}
  <EngagementInvitationDrawer
    summary={selectedSummary}
    invitation={selectedInvitation}
    loading={detailLoading}
    error={detailError}
    acting={actingId === selectedSummary?.id}
    {actingAction}
    actionsDisabled={syncingAcceptedOpportunity}
    onClose={closeDrawer}
    onAccept={handleAccept}
    onReject={handleReject}
  />
{/if}

{#if rejectDialogTarget}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    use:portal
    class="fixed inset-0 flex items-center justify-center px-4"
    style="z-index: 9999; background-color: rgba(0, 0, 0, 0.5);"
    on:click={closeRejectDialog}
  >
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="w-[460px] max-w-[92vw] rounded-2xl bg-[#24283a] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.45)] p-6"
      on:click|stopPropagation
    >
      <div class="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Reject</div>
      <div class="text-xl font-bold text-white leading-tight">
        {rejectDialogTarget.engagementTitle || "Engagement Invitation"}
      </div>
      <div class="text-sm text-white/70 mt-3 leading-6">
        Choose the reason that should be sent back to AWS Partner Central.
      </div>

      <div class="relative mt-5">
        <select
          bind:value={rejectionReason}
          class="w-full appearance-none px-4 py-3 pr-12 bg-[#1b1e2e] border border-white/20 text-white rounded-lg text-sm focus:outline-none focus:border-white/50"
        >
          {#each rejectionReasons as reason}
            <option value={reason}>{reason}</option>
          {/each}
        </select>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-white/70">
          <svg class="h-4 w-4" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M6 8l4 4 4-4"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>

      <div class="mt-6 flex flex-col-reverse sm:flex-row justify-end gap-3">
        <button
          class="px-4 py-3 rounded-lg border border-white/15 text-white/80 hover:bg-[#24283a]/5 transition-colors"
          on:click={closeRejectDialog}
          disabled={actingId === rejectDialogTarget.id || syncingAcceptedOpportunity}
        >
          Cancel
        </button>
        <button
          class="px-4 py-3 rounded-lg border border-red-400/40 text-red-100 bg-red-500/10 hover:bg-red-500/15 transition-colors disabled:opacity-60"
          on:click={confirmReject}
          disabled={actingId === rejectDialogTarget.id || syncingAcceptedOpportunity}
        >
          {actingId === rejectDialogTarget.id && actingAction === "reject" ? "Working..." : "Reject"}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .engagement-banner {
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 1rem;
    background: rgba(0, 0, 0, 0.14);
  }

  .engagement-banner__inner {
    align-items: flex-start;
    display: flex;
    gap: 0.95rem;
    padding: 0.95rem 1.15rem;
  }

  .engagement-banner__icon {
    align-items: center;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 999px;
    display: inline-flex;
    flex-shrink: 0;
    font-size: 0.95rem;
    font-weight: 700;
    height: 2.2rem;
    justify-content: center;
    width: 2.2rem;
  }

  .engagement-banner__content {
    flex: 1;
    min-width: 0;
  }

  .engagement-banner__eyebrow {
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    line-height: 1.2;
    margin: 0;
    text-transform: uppercase;
  }

  .engagement-banner__message {
    color: rgba(255, 255, 255, 0.84);
    font-size: 0.94rem;
    line-height: 1.58;
    margin: 0.45rem 0 0;
  }

  .engagement-banner__close {
    background: transparent;
    border: 0;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.64);
    cursor: pointer;
    flex-shrink: 0;
    font-size: 1.4rem;
    line-height: 1;
    opacity: 0.8;
    padding: 0;
    transition: opacity 150ms ease, background-color 150ms ease;
    width: 1.75rem;
    height: 1.75rem;
  }

  .engagement-banner__close:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.92);
    opacity: 1;
  }

  .engagement-banner--success {
    border-color: rgba(74, 222, 128, 0.3);
    color: rgba(255, 255, 255, 0.96);
  }

  .engagement-banner--success .engagement-banner__icon {
    background: rgba(34, 197, 94, 0.12);
    border-color: rgba(74, 222, 128, 0.24);
    color: #f0fdf4;
  }

  .engagement-banner--success .engagement-banner__eyebrow {
    color: rgba(134, 239, 172, 0.92);
  }

  .engagement-banner--warning {
    border-color: rgba(252, 211, 77, 0.3);
    color: rgba(255, 255, 255, 0.96);
  }

  .engagement-banner--warning .engagement-banner__icon {
    background: rgba(245, 158, 11, 0.14);
    border-color: rgba(252, 211, 77, 0.24);
    color: #fffbeb;
  }

  .engagement-banner--warning .engagement-banner__eyebrow {
    color: rgba(252, 211, 77, 0.94);
  }

  .engagement-banner--error {
    border-color: rgba(248, 113, 113, 0.3);
    color: rgba(255, 255, 255, 0.96);
  }

  .engagement-banner--error .engagement-banner__icon {
    background: rgba(239, 68, 68, 0.14);
    border-color: rgba(248, 113, 113, 0.24);
    color: #fef2f2;
  }

  .engagement-banner--error .engagement-banner__eyebrow {
    color: rgba(252, 165, 165, 0.92);
  }

  @media (max-width: 640px) {
    .engagement-banner__inner {
      gap: 0.85rem;
      padding: 0.95rem 1rem;
    }

    .engagement-banner__message {
      font-size: 0.92rem;
      line-height: 1.55;
    }
  }
</style>

{#if syncDialogTarget && syncDialogResult}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    use:portal
    class="fixed inset-0 flex items-center justify-center px-4"
    style="z-index: 9999; background-color: rgba(0, 0, 0, 0.5);"
    on:click={closeSyncDialog}
  >
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="w-[500px] max-w-[92vw] rounded-2xl bg-[#24283a] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.45)] p-6"
      on:click|stopPropagation
    >
      <div class="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Sync Opportunity</div>
      <div class="text-xl font-bold text-white leading-tight">
        {syncDialogTarget.engagementTitle || "Accepted Engagement Invitation"}
      </div>
      <div class="text-sm text-white/90 mt-3 leading-6">
        The invitation was accepted successfully. Do you want to import the new AWS opportunity
        and then try to sync it to HubSpot now?
      </div>

      <div class="mt-4 rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white/90 leading-6">
        {syncDialogResult.message ||
          "Invitation accepted. Use Sync from AWS later if you do not want to sync it now."}
      </div>

      <div class="mt-6 flex flex-col-reverse sm:flex-row justify-end gap-3">
        <button
          class="px-4 py-3 rounded-lg border border-white/15 text-white/80 hover:bg-[#24283a]/5 transition-colors"
          on:click={deferAcceptedSync}
          disabled={syncingAcceptedOpportunity}
        >
          Later
        </button>
        <button
          class="px-4 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500 transition-colors disabled:opacity-60"
          on:click={confirmAcceptedSync}
          disabled={syncingAcceptedOpportunity}
        >
          {syncingAcceptedOpportunity ? "Syncing..." : "Sync Now"}
        </button>
      </div>
    </div>
  </div>
{/if}
